import { useEffect, useState } from "react"
import { Link, generatePath } from "react-router-dom"
import { X } from "@phosphor-icons/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChartLine,
  faPen,
  faPlus,
  faQrcode,
} from "@fortawesome/free-solid-svg-icons"
import ApiError from "../../components/ApiError"
import Modal from "../../components/Modal"
import EventQrPreview from "../../components/EventQrPreview"
import routePaths from "../../constants/path"
import {
  useEventQrCodes,
  useSetEventQrCodeActive,
} from "../../hooks/useEventQr"
import EventQrCodeModal from "./EventQrCodeModal"
import {
  EventStatusPill,
  ScrollTable,
  TableCell,
  TableMessageRow,
  TablePagination,
  ToggleSwitch,
  formatEventDate,
  useCanEditEventQr,
} from "./shared"

const columns = [
  { key: "event", label: "Event", width: "w-[22%]" },
  { key: "status", label: "Status", width: "w-[9%]" },
  { key: "starts", label: "Starts", width: "w-[12%]" },
  { key: "expires", label: "Expires", width: "w-[12%]" },
  { key: "videos", label: "Videos", width: "w-[7%]", className: "text-right" },
  { key: "scans", label: "Scans", width: "w-[12%]", className: "text-right" },
  {
    key: "feedback",
    label: "Feedback",
    width: "w-[8%]",
    className: "text-right",
  },
  { key: "on", label: "On", width: "w-[6%]" },
  {
    key: "actions",
    label: "Actions",
    width: "w-[12%]",
    className: "text-right",
  },
]

const IconButton = ({ icon, label, onClick }) => (
  <button
    aria-label={label}
    className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
    title={label}
    type="button"
    onClick={onClick}
  >
    <FontAwesomeIcon className="h-3.5 w-3.5" icon={icon} />
  </button>
)

const QrModal = ({ event, onClose }) => (
  <Modal className="max-w-sm" isOpen={!!event} onClose={onClose}>
    <div className="mb-4 flex items-center justify-between">
      <h2 className="truncate text-lg font-semibold text-gray-900">
        {event?.eventName}
      </h2>
      <button
        className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        type="button"
        onClick={onClose}
      >
        <X size={20} />
      </button>
    </div>
    {event && (
      <EventQrPreview
        eventName={event.eventName}
        publicUuid={event.publicUuid}
        size={240}
      />
    )}
  </Modal>
)

const SEARCH_DEBOUNCE_MS = 300

const EventQrCodesTab = () => {
  const canEdit = useCanEditEventQr()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")

  // Searches once typing pauses, and from the first page.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeoutId)
  }, [searchInput])

  const { data, isLoading, isFetching, isError, error } = useEventQrCodes({
    page,
    perPage,
    search,
  })
  const events = data?.data || []
  const total = data?.total || 0
  const setActiveMutation = useSetEventQrCodeActive()

  // undefined = closed, null = create, number = edit that event.
  const [editingId, setEditingId] = useState(undefined)
  const [qrEvent, setQrEvent] = useState(null)

  // Deleting isn't possible, but a search can shrink the list under the
  // current page; step back to the last page that exists.
  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(total / perPage))
    if (data && page > pageCount) setPage(pageCount)
  }, [data, total, perPage, page])

  if (isError) return <ApiError axiosError={error} />

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="flex w-full max-w-md items-center gap-3">
          <input
            className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 text-sm outline-0 focus:border-secondary"
            placeholder="Search event name…"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          {isFetching && !isLoading && (
            <span className="shrink-0 text-xs text-gray-400">Updating…</span>
          )}
        </div>
        {canEdit && (
          <button
            className="flex shrink-0 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            type="button"
            onClick={() => setEditingId(null)}
          >
            <FontAwesomeIcon className="h-3 w-3" icon={faPlus} />
            New QR code
          </button>
        )}
      </div>

      <ScrollTable columns={columns}>
        {isLoading && (
          <TableMessageRow colSpan={columns.length}>Loading…</TableMessageRow>
        )}
        {!isLoading && !events.length && (
          <TableMessageRow colSpan={columns.length}>
            {search ? "No events match." : "No event QR codes yet."}
          </TableMessageRow>
        )}
        {events.map((event) => (
          <tr key={event.id} className="hover:bg-gray-50">
            <TableCell>
              <div className="font-medium text-gray-900">{event.eventName}</div>
              <div className="text-xs text-gray-400">
                Created {formatEventDate(event.createdAt)}
              </div>
            </TableCell>
            <TableCell>
              <EventStatusPill status={event.status} />
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {formatEventDate(event.startsAt, "Right away")}
            </TableCell>
            <TableCell className="whitespace-nowrap">
              {formatEventDate(event.expiresAt, "Never")}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {event.videoCount}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {event.scanCount.toLocaleString()}
              <span className="ml-1 text-xs text-gray-400">
                ({event.uniqueVisitors.toLocaleString()} unique)
              </span>
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {event.feedbackCount.toLocaleString()}
            </TableCell>
            <TableCell>
              <ToggleSwitch
                checked={event.isActive}
                disabled={!canEdit || setActiveMutation.isPending}
                label={`Turn ${event.eventName} ${event.isActive ? "off" : "on"}`}
                onChange={() =>
                  setActiveMutation.mutate({
                    id: event.id,
                    isActive: !event.isActive,
                  })
                }
              />
            </TableCell>
            <TableCell className="whitespace-nowrap text-right">
              <IconButton
                icon={faQrcode}
                label="Show QR code"
                onClick={() => setQrEvent(event)}
              />
              {canEdit && (
                <IconButton
                  icon={faPen}
                  label="Edit"
                  onClick={() => setEditingId(event.id)}
                />
              )}
              <Link
                aria-label="Results"
                className="inline-block rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                title="Results"
                to={generatePath(routePaths.eventQrCodeDetail, {
                  id: event.id,
                })}
              >
                <FontAwesomeIcon className="h-3.5 w-3.5" icon={faChartLine} />
              </Link>
            </TableCell>
          </tr>
        ))}
      </ScrollTable>

      <TablePagination
        page={page}
        perPage={perPage}
        total={total}
        onPageChange={setPage}
        onPerPageChange={(size) => {
          setPerPage(size)
          setPage(1)
        }}
      />

      <EventQrCodeModal
        eventId={editingId ?? null}
        isOpen={editingId !== undefined}
        onClose={() => setEditingId(undefined)}
      />
      <QrModal event={qrEvent} onClose={() => setQrEvent(null)} />
    </div>
  )
}

export default EventQrCodesTab
